// frontend/src/pages/CollectPage.jsx
// GPS Data Collection — tap map to place pole, fill details, save
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { fetchPoles, fetchPoleTypes, createPole, updatePole } from "../services/api";

const DEFAULT_CENTER = { lat: 7.096033, lng: 80.020300 };

// ── Auto-generate next pole code
function nextPoleCode(poles) {
  if (!poles.length) return "P001";
  const nums = poles
    .map(p => parseInt(p.pole_code?.replace(/\D/g, "")) || 0)
    .filter(n => !isNaN(n));
  const next = Math.max(...nums, 0) + 1;
  return `P${String(next).padStart(3, "0")}`;
}

// ── Small field component
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 10, color: "#64748b", display: "block", marginBottom: 5, letterSpacing: "0.1em" }}>
        {label}{required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const INPUT = {
  width: "100%", background: "#0a1628", border: "1px solid #1e3a2e",
  borderRadius: 8, padding: "10px 12px", color: "#e2e8f0", fontSize: 14,
  outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const SELECT = { ...INPUT };

export default function CollectPage() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const editId         = params.get("edit"); // /collect?edit=5 for editing

  const currentUser    = JSON.parse(localStorage.getItem("user") || "{}");
  const canEdit        = ["admin", "supervisor"].includes(currentUser.role);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  const [poles, setPoles]           = useState([]);
  const [poleTypes, setPoleTypes]   = useState([]);
  const [mapCenter, setMapCenter]   = useState(DEFAULT_CENTER);
  const [markerPos, setMarkerPos]   = useState(null);
  const [userPos, setUserPos]       = useState(null);
  const [gpsStatus, setGpsStatus]   = useState("acquiring"); // acquiring | ok | error
  const [mapLoaded, setMapLoaded]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [msg, setMsg]               = useState({ text: "", ok: false });
  const watchRef                    = useRef(null);

  const emptyForm = {
    pole_code: "", name: "", description: "",
    panel_watt: "150", pole_type_id: "",
    road_side: "Left", install_date: "",
    status: "Active", latitude: "", longitude: "",
  };
  const [form, setForm] = useState(emptyForm);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Load poles + pole types
  useEffect(() => {
    fetchPoles().then(r => {
      const data = r.data.data;
      setPoles(data);
      if (!editId) set("pole_code", nextPoleCode(data));
    }).catch(console.error);

    fetchPoleTypes().then(r => setPoleTypes(r.data.data)).catch(console.error);
  }, []);

  // ── If editing, load existing pole data
  useEffect(() => {
    if (!editId || !poles.length) return;
    const pole = poles.find(p => String(p.id) === editId);
    if (!pole) return;
    setForm({
      pole_code:    pole.pole_code,
      name:         pole.name,
      description:  pole.description || "",
      panel_watt:   pole.panel_watt || "150",
      pole_type_id: pole.pole_type_id || "",
      road_side:    pole.road_side || "Left",
      install_date: pole.install_date ? pole.install_date.split("T")[0] : "",
      status:       pole.status || "Active",
      latitude:     pole.latitude || "",
      longitude:    pole.longitude || "",
    });
    const pos = { lat: parseFloat(pole.latitude), lng: parseFloat(pole.longitude) };
    setMarkerPos(pos);
    setMapCenter(pos);
  }, [editId, poles]);

  // ── GPS watch
  useEffect(() => {
    if (!navigator.geolocation) { setGpsStatus("error"); return; }
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (accuracy > 100) return;
        const p = { lat: latitude, lng: longitude };
        setUserPos(p);
        setGpsStatus("ok");
        // On first fix, centre map on user
        if (!markerPos) setMapCenter(p);
      },
      () => setGpsStatus("error"),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
    );
    return () => { if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current); };
  }, []);

  // ── Tap on map to place marker
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const pos = { lat, lng };
    setMarkerPos(pos);
    set("latitude",  lat.toFixed(7));
    set("longitude", lng.toFixed(7));
  }, []);

  // ── Use current GPS position
  const useGPSLocation = () => {
    if (!userPos) return;
    setMarkerPos(userPos);
    setMapCenter(userPos);
    set("latitude",  userPos.lat.toFixed(7));
    set("longitude", userPos.lng.toFixed(7));
  };

  // ── Save
  const handleSave = async () => {
    setMsg({ text: "", ok: false });
    if (!form.pole_code) return setMsg({ text: "Pole code is required", ok: false });
    if (!form.name)      return setMsg({ text: "Pole name is required", ok: false });
    if (!form.latitude || !form.longitude)
      return setMsg({ text: "Please place the pole on the map first", ok: false });

    setSaving(true);
    try {
      if (editId) {
        await updatePole(editId, form);
        setMsg({ text: "✅ Pole updated successfully!", ok: true });
      } else {
        await createPole(form);
        setMsg({ text: "✅ Pole saved successfully!", ok: true });
        setSaved(true);
        // Reset for next pole
        const newPoles = await fetchPoles().then(r => r.data.data);
        setPoles(newPoles);
        setForm({ ...emptyForm, pole_code: nextPoleCode(newPoles) });
        setMarkerPos(null);
      }
    } catch (err) {
      setMsg({ text: err.response?.data?.message || "Failed to save pole", ok: false });
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div style={{ minHeight: "100vh", background: "#0a0f1a", display: "flex", alignItems: "center", justifyContent: "center", color: "#f87171", fontFamily: "monospace" }}>
        ❌ Access denied. Admin or Supervisor role required.
      </div>
    );
  }

  return (
    <div style={{ height: "100dvh", background: "#0a0f1a", fontFamily: "'JetBrains Mono',monospace", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Syne:wght@700;800&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1e3a2e; border-radius: 4px; }
        @media (min-width: 768px) {
          .collect-layout { flex-direction: row !important; }
          .collect-map    { flex: 1 !important; height: 100% !important; }
          .collect-form   { width: 360px !important; height: 100% !important; overflow-y: auto; }
        }
      `}</style>

      {/* ── Top Bar ── */}
      <div style={{ background: "linear-gradient(135deg,#0d2010,#0a1628)", borderBottom: "1px solid #1a2e1a",
        padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate("/dashboard")}
            style={{ background: "transparent", border: "1px solid #1e3a2e", color: "#4ade80", borderRadius: 8, padding: "5px 10px", fontSize: 11, cursor: "pointer" }}>
            ← Back
          </button>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, color: "#4ade80" }}>
              {editId ? "✏️ Edit Pole" : "📍 Collect Pole Location"}
            </div>
            <div style={{ fontSize: 9, color: "#4b5563", letterSpacing: "0.1em" }}>TAP MAP TO PLACE · FILL DETAILS · SAVE</div>
          </div>
        </div>
        {/* GPS Status */}
        <div style={{ fontSize: 11, textAlign: "right" }}>
          {gpsStatus === "ok" && userPos
            ? <span style={{ color: "#4ade80" }}>📡 GPS OK</span>
            : gpsStatus === "error"
              ? <span style={{ color: "#f87171" }}>❌ No GPS</span>
              : <span style={{ color: "#f59e0b" }}>📡 Acquiring...</span>}
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className="collect-layout" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ── MAP ── */}
        <div className="collect-map" style={{ height: "45%", position: "relative", flexShrink: 0 }}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={20}
              mapTypeId="satellite"
              onClick={handleMapClick}
              onLoad={() => setMapLoaded(true)}
            >
              {/* Existing poles — grey markers */}
              {poles.filter(p => !editId || String(p.id) !== editId).map(pole => (
                <Marker key={pole.id}
                  position={{ lat: parseFloat(pole.latitude), lng: parseFloat(pole.longitude) }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6, fillColor: "#6b7280", fillOpacity: 0.8,
                    strokeColor: "#fff", strokeWeight: 1,
                  }}
                  label={{ text: pole.pole_code, color: "#9ca3af", fontSize: "9px" }}
                />
              ))}

              {/* New pole marker — bright green */}
              {markerPos && (
                <Marker
                  position={markerPos}
                  draggable={true}
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    setMarkerPos({ lat, lng });
                    set("latitude",  lat.toFixed(7));
                    set("longitude", lng.toFixed(7));
                  }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 12, fillColor: "#4ade80", fillOpacity: 1,
                    strokeColor: "#fff", strokeWeight: 2,
                  }}
                  label={{ text: form.pole_code || "NEW", color: "#fff", fontSize: "10px", fontWeight: "bold" }}
                />
              )}

              {/* User position — blue */}
              {userPos && (
                <Marker position={userPos}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 8, fillColor: "#3b82f6", fillOpacity: 1,
                    strokeColor: "#fff", strokeWeight: 2,
                  }}
                  title="Your location"
                />
              )}
            </GoogleMap>
          ) : (
            <div style={{ width: "100%", height: "100%", background: "#060c12", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
              <div style={{ width: 32, height: 32, border: "3px solid #22c55e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              <div style={{ color: "#4b5563", fontSize: 12 }}>Loading map...</div>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* Map hint overlay */}
          {mapLoaded && !markerPos && (
            <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.75)", borderRadius: 20, padding: "6px 14px", fontSize: 12, color: "#e2e8f0", pointerEvents: "none", whiteSpace: "nowrap" }}>
              👆 Tap map to place pole
            </div>
          )}

          {/* GPS button */}
          {userPos && (
            <button onClick={useGPSLocation}
              style={{ position: "absolute", bottom: 12, right: 12, background: "#0d1520", border: "1px solid #22c55e",
                color: "#4ade80", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              📍 Use My GPS
            </button>
          )}

          {/* Coordinates display */}
          {markerPos && (
            <div style={{ position: "absolute", bottom: 12, left: 12, background: "rgba(0,0,0,0.8)",
              borderRadius: 8, padding: "6px 10px", fontSize: 11, color: "#4ade80" }}>
              {markerPos.lat.toFixed(6)}°N, {markerPos.lng.toFixed(6)}°E
            </div>
          )}
        </div>

        {/* ── FORM ── */}
        <div className="collect-form" style={{ flex: 1, overflowY: "auto", padding: "14px 16px", background: "#0d1520", borderTop: "1px solid #1a2e1a" }}>

          {/* Pole Code + Status row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="POLE CODE" required>
              <input style={INPUT} value={form.pole_code} onChange={e => set("pole_code", e.target.value)} placeholder="P001" />
            </Field>
            <Field label="STATUS">
              <select style={SELECT} value={form.status} onChange={e => set("status", e.target.value)}>
                {["Active","Maintenance","Inactive","Pending"].map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="POLE NAME" required>
            <input style={INPUT} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Solar Pole P01" />
          </Field>

          {/* Pole Type — dynamic dropdown */}
          <Field label="POLE TYPE">
            <select style={SELECT} value={form.pole_type_id} onChange={e => set("pole_type_id", e.target.value)}>
              <option value="">— Select Pole Type —</option>
              {poleTypes.map(t => (
                <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="DESCRIPTION">
            <textarea style={{ ...INPUT, minHeight: 60, resize: "vertical" }}
              value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="Location details, landmarks, notes..." />
          </Field>

          {/* Panel Watt + Road Side */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="PANEL WATT">
              <input style={INPUT} type="number" value={form.panel_watt} onChange={e => set("panel_watt", e.target.value)} />
            </Field>
            <Field label="ROAD SIDE">
              <select style={SELECT} value={form.road_side} onChange={e => set("road_side", e.target.value)}>
                {["Left","Right","North","South","East","West"].map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="INSTALL DATE">
            <input style={INPUT} type="date" value={form.install_date} onChange={e => set("install_date", e.target.value)} />
          </Field>

          {/* GPS Coordinates — read only, set by map tap */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="LATITUDE">
              <input style={{ ...INPUT, color: form.latitude ? "#4ade80" : "#4b5563" }}
                value={form.latitude} onChange={e => {
                  set("latitude", e.target.value);
                  const lat = parseFloat(e.target.value);
                  const lng = parseFloat(form.longitude);
                  if (!isNaN(lat) && !isNaN(lng)) { setMarkerPos({ lat, lng }); setMapCenter({ lat, lng }); }
                }} placeholder="Tap map..." />
            </Field>
            <Field label="LONGITUDE">
              <input style={{ ...INPUT, color: form.longitude ? "#4ade80" : "#4b5563" }}
                value={form.longitude} onChange={e => {
                  set("longitude", e.target.value);
                  const lat = parseFloat(form.latitude);
                  const lng = parseFloat(e.target.value);
                  if (!isNaN(lat) && !isNaN(lng)) { setMarkerPos({ lat, lng }); setMapCenter({ lat, lng }); }
                }} placeholder="Tap map..." />
            </Field>
          </div>

          {/* Message */}
          {msg.text && (
            <div style={{ padding: "10px 14px", borderRadius: 10, marginBottom: 12, fontSize: 13,
              background: msg.ok ? "#14532d" : "#450a0a",
              color:      msg.ok ? "#86efac"  : "#fca5a5",
              border:     `1px solid ${msg.ok ? "#22c55e" : "#7f1d1d"}` }}>
              {msg.text}
            </div>
          )}

          {/* Save button */}
          <button onClick={handleSave} disabled={saving}
            style={{ width: "100%", background: saving ? "#166534" : "linear-gradient(135deg,#16a34a,#15803d)",
              color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontSize: 14,
              fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", marginBottom: 8 }}>
            {saving ? "⏳ Saving..." : editId ? "💾 Update Pole" : "💾 Save Pole"}
          </button>

          {!editId && saved && (
            <button onClick={() => { setSaved(false); setMsg({ text: "", ok: false }); }}
              style={{ width: "100%", background: "#0a1628", border: "1px solid #1e3a2e",
                color: "#94a3b8", borderRadius: 10, padding: "10px", fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
              ➕ Add Another Pole
            </button>
          )}

          <button onClick={() => navigate("/dashboard")}
            style={{ width: "100%", background: "transparent", border: "1px solid #1e3a2e",
              color: "#64748b", borderRadius: 10, padding: "10px", fontSize: 12, cursor: "pointer", marginBottom: 20 }}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
